import {
  createSession,
  getEmptySessions,
  initializeDbWebSocket,
  initializeOlWebSocket,
  initializeReplayWebSocket,
  Sessions,
} from '@ais-ui/auth';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useToast } from '@ais-ui/ui-core';
import * as Sentry from '@sentry/react';
type SessionType = 'dbSession' | 'replaySession' | 'olSession';

type ServerConfigs = {
  [key in SessionType]: {
    initialize: (hostname: string, protocol: string) => Promise<any>;
    name: SessionType;
    maxRetries?: number;
    maxBackoffDelay?: number;
  };
};

// Helper function to determine if we should log this reconnection attempt
const shouldLogReconnectionAttempt = (retryCount: number): boolean => {
  // Log first 5 attempts (0-4)
  if (retryCount < 5) {
    return true;
  }
  // Log every 10th attempt from 5 to 99
  if (retryCount < 100 && retryCount % 10 === 0) {
    return true;
  }
  // Log every 100th attempt from 100 onwards
  if (retryCount >= 100 && retryCount % 100 === 0) {
    return true;
  }
  return false;
};

export const useReconnect = (
  sessions: Sessions,
  setSessions: Dispatch<SetStateAction<Sessions>>,
  toast: ReturnType<typeof useToast>['toast'],
  logout: (reason: string) => void,
) => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Configuration for each server type
  const serverConfigs: ServerConfigs = useMemo(
    () => ({
      dbSession: {
        initialize: initializeDbWebSocket,
        name: 'dbSession',
        maxRetries: Infinity,
        maxBackoffDelay: 180000, // 3 minutes
      },
      replaySession: {
        initialize: initializeReplayWebSocket,
        name: 'replaySession',
        maxRetries: Infinity,
        maxBackoffDelay: 180000, // 3 minutes
      },
      olSession: {
        initialize: initializeOlWebSocket,
        name: 'olSession',
        maxRetries: 10,
        maxBackoffDelay: 10000, // 10 seconds
      },
    }),
    [],
  );

  const reconnectSession = useCallback(
    async (sessionType: SessionType, retryCount = 0) => {
      const config = serverConfigs[sessionType];

      // Don't attempt reconnection if there's no sessionId
      if (!sessions?.sessionId) {
        console.warn(`Cannot reconnect ${sessionType}: No sessionId available`);
        Sentry.captureMessage(
          `Reconnection attempt blocked for ${sessionType}: No sessionId`,
          'warning',
        );
        return;
      }

      try {
        const { webSocket, serverAndClient } = await config.initialize(
          hostname,
          protocol,
        );

        // Handle connection failure
        if (!webSocket || !serverAndClient) {
          // Check if we've reached max retries for olserver
          if (
            config.maxRetries !== undefined &&
            retryCount >= config.maxRetries
          ) {
            Sentry.captureMessage(
              `Max reconnection attempts (${config.maxRetries}) reached for ${sessionType}`,
              'error',
            );

            // The application cannot run without the olserver
            if (sessionType === 'olSession') {
              Sentry.captureMessage(
                `Max reconnection attempts reached for ${sessionType}, forcing logout`,
                'error',
              );
              logout(
                'Could not reconnect to the server after multiple attempts',
              );
            }
            return;
          }

          // Calculate retry delay with exponential backoff
          const delay = Math.min(
            1000 * 2 ** retryCount,
            config.maxBackoffDelay || 180000,
          );

          // Only log if this attempt should be logged
          if (shouldLogReconnectionAttempt(retryCount)) {
            console.warn(
              `Reconnection attempt ${
                retryCount + 1
              } failed to connect to ${sessionType}, retrying in ${
                delay / 1000
              } seconds`,
            );

            Sentry.captureMessage(
              `Reconnection attempt ${retryCount + 1} failed for ${sessionType}`,
              'warning',
            );
          }

          // Retry after the delay
          setTimeout(() => {
            reconnectSession(sessionType, retryCount + 1);
          }, delay);
          return;
        }

        // Create the session object
        const session = createSession(
          serverAndClient,
          webSocket,
          sessions.sessionId,
          config.name,
        );

        if (!session) {
          Sentry.captureMessage(
            `Failed to create ${sessionType} session on attempt ${
              retryCount + 1
            }`,
            'error',
          );
          console.warn(
            `Reconnection attempt ${
              retryCount + 1
            } failed to create ${sessionType} session`,
          );
          return;
        }

        // Update the sessions state
        setSessions((prevSessions) => {
          if (!prevSessions) return getEmptySessions();
          return {
            ...prevSessions,
            [config.name]: session,
          };
        });

        toast({
          className: 'reconnect-success-toast',
          title: 'Reconnected',
          description: `Reconnected to ${config.name} after lost connection`,
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            serverType: sessionType,
            retryCount,
          },
          extra: {
            sessionId: sessions?.sessionId,
          },
        });

        // Catch any errors and continue retrying if within limits
        if (config.maxRetries === undefined || retryCount < config.maxRetries) {
          const delay = Math.min(
            1000 * 2 ** retryCount,
            config.maxBackoffDelay || 180000,
          );

          // Only log if this attempt should be logged
          if (shouldLogReconnectionAttempt(retryCount)) {
            console.warn(
              `Error during reconnection attempt ${
                retryCount + 1
              } to ${sessionType}, retrying in ${delay / 1000} seconds`,
              error,
            );
          }

          setTimeout(() => {
            reconnectSession(sessionType, retryCount + 1);
          }, delay);
        } else if (sessionType === 'olSession') {
          logout('Could not reconnect to the server after multiple attempts');
        }
      }
    },
    [hostname, protocol, sessions, setSessions, toast, logout, serverConfigs],
  );

  const reconnect = useCallback(
    (sessionType: string) => {
      if (
        sessionType === 'dbSession' ||
        sessionType === 'replaySession' ||
        sessionType === 'olSession'
      ) {
        return reconnectSession(sessionType);
      } else {
        console.warn('Unknown session type, not reconnecting');
        Sentry.captureMessage(
          `Attempted to reconnect to unknown session type: ${sessionType}`,
          'error',
        );
      }
    },
    [reconnectSession],
  );

  return reconnect;
};
